import { collection, doc, getDocs, query, runTransaction, where } from 'firebase/firestore';
import { logger } from './logger';
import { storage } from './storage';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { getFirebaseAuth, getFirestoreDb } from './firebase';
import type { PromptData, FolderData } from '../types/storage';

/**
 * Firestore-direct sync (replaces the Hono/Vercel backend client).
 *
 * The extension reads and writes the signed-in user's data directly in
 * Firestore under `users/{uid}/prompts` and `users/{uid}/folders`, enforced by
 * security rules (`request.auth.uid == uid`). Local collections
 * (`STORAGE_KEYS.PROMPTS`/`FOLDERS`) are unchanged; per-item sync metadata
 * (version, last_modified, content hash, deletion, cursor) lives under
 * `STORAGE_KEYS.API_SYNC_STATE`.
 *
 * Conflict resolution is per-item last-write-wins by `version` then
 * `last_modified`, applied on BOTH ends:
 *  - push runs a Firestore transaction so a stale local copy can't overwrite a
 *    newer remote doc (the loser is pulled back instead);
 *  - soft-deletes (`is_deleted: true` docs) are the tombstones — they sync like
 *    any other change, so a deletion can't be resurrected by a stale device.
 * Pull changes are applied onto a FRESH local read so concurrent edits and
 * local-only fields (e.g. usageCount) are preserved, not clobbered.
 */

interface PromptDoc {
  title: string;
  text: string;
  description: string;
  tags: string[];
  folder: string;
  comments: string;
  last_modified: number;
  version: number;
  is_deleted: boolean;
}
interface FolderDoc {
  name: string;
  color: string;
  is_special: boolean;
  icon: string | null;
  type: 'regular' | 'special' | null;
  parent_id: string | null;
  order: number | null;
  last_modified: number;
  version: number;
  is_deleted: boolean;
}

interface ItemMeta {
  version: number;
  lastModified: number;
  hash: string;
  deleted?: boolean;
}
interface SyncState {
  /** Account this state belongs to — a cursor/version map from one account must
   *  never be reused for another (missed pulls, cross-account version carry-over). */
  uid?: string;
  cursor: number; // max last_modified pulled so far (ms)
  prompts: Record<string, ItemMeta>;
  folders: Record<string, ItemMeta>;
}

function emptyState(uid?: string): SyncState {
  return { uid, cursor: 0, prompts: {}, folders: {} };
}

/** LWW: incoming wins only on a strict version/timestamp advance. Ties keep existing. */
function shouldApply(
  existing: { version: number; lastModified: number } | undefined,
  incoming: { version: number; lastModified: number }
): boolean {
  if (!existing) return true;
  if (incoming.version !== existing.version) return incoming.version > existing.version;
  return incoming.lastModified > existing.lastModified;
}

function hashPrompt(p: PromptData): string {
  return JSON.stringify([p.title, p.text, p.description, p.tags, p.folder, p.comments]);
}
function folderIsSpecial(f: FolderData): boolean {
  // Repository-shaped folders mark specialness via `type`, UI-shaped ones via `isSpecial`.
  return !!(f.isSpecial ?? f.type === 'special');
}
function hashFolder(f: FolderData): string {
  return JSON.stringify([
    f.name,
    f.color,
    folderIsSpecial(f),
    f.icon ?? '',
    f.type ?? '',
    f.parentId ?? '',
    typeof f.order === 'number' ? f.order : null,
  ]);
}

function docToPrompt(id: string, d: PromptDoc): PromptData {
  return {
    id,
    title: d.title ?? '',
    text: d.text ?? '',
    description: d.description ?? '',
    tags: Array.isArray(d.tags) ? d.tags : [],
    folder: d.folder ?? '',
    lastChanged: d.last_modified || Date.now(),
    comments: d.comments ?? '',
  };
}
function docToFolder(id: string, d: FolderDoc): FolderData {
  const f: FolderData = {
    id,
    name: d.name ?? '',
    color: d.color ?? '',
    isSpecial: !!d.is_special || d.type === 'special',
    icon: d.icon ?? undefined,
  };
  if (d.type === 'regular' || d.type === 'special') f.type = d.type;
  if (typeof d.parent_id === 'string' && d.parent_id) f.parentId = d.parent_id;
  if (typeof d.order === 'number') f.order = d.order;
  return f;
}
function promptToDoc(p: PromptData, version: number, lm: number, del: boolean): PromptDoc {
  return {
    title: p.title,
    text: p.text,
    description: p.description ?? '',
    tags: p.tags ?? [],
    folder: p.folder || '',
    comments: p.comments ?? '',
    last_modified: lm,
    version,
    is_deleted: del,
  };
}
function folderToDoc(f: FolderData, version: number, lm: number, del: boolean): FolderDoc {
  return {
    name: f.name,
    color: f.color ?? '',
    is_special: folderIsSpecial(f),
    icon: f.icon ?? null,
    type: f.type === 'regular' || f.type === 'special' ? f.type : null,
    parent_id: f.parentId ?? null,
    order: typeof f.order === 'number' ? f.order : null,
    last_modified: lm,
    version,
    is_deleted: del,
  };
}

let inFlight: Promise<void> | null = null;

/** Run one full sync cycle (push local changes via LWW, then pull + merge). */
export function syncNow(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = runSync().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function runSync(): Promise<void> {
  const uid = getFirebaseAuth().currentUser?.uid;
  if (!uid) return;
  const db = getFirestoreDb();

  let state: SyncState =
    ((await storage.get(STORAGE_KEYS.API_SYNC_STATE)) as SyncState) ?? emptyState(uid);
  state.prompts ??= {};
  state.folders ??= {};
  let stateDirty = false;

  // Account switch without a clean sign-out: the local library and sync state
  // belong to the previous account. Clear both rather than pushing the previous
  // user's data into this account (their copy still lives in their own cloud).
  if (state.uid && state.uid !== uid) {
    logger.warning(
      'Sync state belongs to a different account; clearing local library and sync scope',
      {
        module: 'firestoreSync',
        function: 'runSync',
      }
    );
    await storage.set(STORAGE_KEYS.PROMPTS, []);
    await storage.set(STORAGE_KEYS.FOLDERS, []);
    state = emptyState(uid);
    stateDirty = true;
  } else if (state.uid !== uid) {
    // Legacy state without a uid — adopt the current account.
    state.uid = uid;
    stateDirty = true;
  }

  const localPrompts = ((await storage.get(STORAGE_KEYS.PROMPTS)) ?? []) as PromptData[];
  const localFolders = ((await storage.get(STORAGE_KEYS.FOLDERS)) ?? []) as FolderData[];

  const now = Date.now();
  const promptsById = new Map(localPrompts.map((p): [string, PromptData] => [p.id, p]));
  const foldersById = new Map(localFolders.map((f): [string, FolderData] => [f.id, f]));

  // Changes the pull/conflict steps want to make, applied onto a fresh read (§persist).
  const promptUpserts = new Map<string, PromptData>();
  const promptDeletes = new Set<string>();
  const folderUpserts = new Map<string, FolderData>();
  const folderDeletes = new Set<string>();
  let maxLm = state.cursor;

  // --- 1. Push local changes + deletions, each under an LWW transaction -----
  const promptsCol = `users/${uid}/prompts`;
  const foldersCol = `users/${uid}/folders`;

  for (const p of localPrompts) {
    const meta = state.prompts[p.id];
    const hash = hashPrompt(p);
    if (meta && !meta.deleted && meta.hash === hash) continue; // unchanged
    const version = meta ? meta.version + 1 : 1;
    // Always stamp with push time — an item's own (possibly days-old) lastChanged
    // would land behind other devices' pull cursors and never propagate.
    const lm = now;
    await pushPrompt(
      db,
      promptsCol,
      p.id,
      promptToDoc(p, version, lm, false),
      { version, lastModified: lm },
      {
        onApplied: () => {
          state.prompts[p.id] = { version, lastModified: lm, hash };
          stateDirty = true;
        },
        onRemoteWins: rd => {
          if (
            applyRemotePrompt(
              p.id,
              rd,
              state,
              promptUpserts,
              promptDeletes,
              v => (maxLm = Math.max(maxLm, v))
            )
          )
            stateDirty = true;
        },
      }
    );
  }
  for (const f of localFolders) {
    const meta = state.folders[f.id];
    const hash = hashFolder(f);
    if (meta && !meta.deleted && meta.hash === hash) continue;
    const version = meta ? meta.version + 1 : 1;
    await pushFolder(
      db,
      foldersCol,
      f.id,
      folderToDoc(f, version, now, false),
      { version, lastModified: now },
      {
        onApplied: () => {
          state.folders[f.id] = { version, lastModified: now, hash };
          stateDirty = true;
        },
        onRemoteWins: rd => {
          if (
            applyRemoteFolder(
              f.id,
              rd,
              state,
              folderUpserts,
              folderDeletes,
              v => (maxLm = Math.max(maxLm, v))
            )
          )
            stateDirty = true;
        },
      }
    );
  }
  // Deletions: tracked-and-not-deleted ids that vanished locally → soft-delete doc.
  // Guarded: a blank/failed local read looks identical to "the user deleted
  // everything at once". When most tracked items vanish in a single cycle,
  // assume a bad read and skip deletion propagation rather than tombstoning
  // the user's entire cloud library.
  const liveTrackedPrompts = Object.entries(state.prompts).filter(([, m]) => !m.deleted);
  const vanishedPrompts = liveTrackedPrompts.filter(([id]) => !promptsById.has(id));
  const liveTrackedFolders = Object.entries(state.folders).filter(([, m]) => !m.deleted);
  const vanishedFolders = liveTrackedFolders.filter(([id]) => !foldersById.has(id));
  const promptDeletionsSuspicious =
    vanishedPrompts.length > 0 &&
    (localPrompts.length === 0 ||
      (vanishedPrompts.length >= 3 && vanishedPrompts.length * 2 > liveTrackedPrompts.length));
  const folderDeletionsSuspicious =
    vanishedFolders.length > 0 &&
    (localFolders.length === 0 ||
      (vanishedFolders.length >= 3 && vanishedFolders.length * 2 > liveTrackedFolders.length));

  if (promptDeletionsSuspicious || folderDeletionsSuspicious) {
    logger.warning('Skipping deletion propagation: implausible mass deletion detected locally', {
      module: 'firestoreSync',
      function: 'runSync',
      vanishedPrompts: vanishedPrompts.length,
      trackedPrompts: liveTrackedPrompts.length,
      vanishedFolders: vanishedFolders.length,
      trackedFolders: liveTrackedFolders.length,
    });
  }
  if (!promptDeletionsSuspicious) {
    for (const [id, meta] of vanishedPrompts) {
      const version = meta.version + 1;
      await pushPrompt(
        db,
        promptsCol,
        id,
        deletedPromptDoc(version, now),
        { version, lastModified: now },
        {
          onApplied: () => {
            state.prompts[id] = { version, lastModified: now, hash: '', deleted: true };
            stateDirty = true;
          },
          onRemoteWins: rd => {
            if (
              applyRemotePrompt(
                id,
                rd,
                state,
                promptUpserts,
                promptDeletes,
                v => (maxLm = Math.max(maxLm, v))
              )
            )
              stateDirty = true;
          },
        }
      );
    }
  }
  if (!folderDeletionsSuspicious) {
    for (const [id, meta] of vanishedFolders) {
      const version = meta.version + 1;
      await pushFolder(
        db,
        foldersCol,
        id,
        deletedFolderDoc(version, now),
        { version, lastModified: now },
        {
          onApplied: () => {
            state.folders[id] = { version, lastModified: now, hash: '', deleted: true };
            stateDirty = true;
          },
          onRemoteWins: rd => {
            if (
              applyRemoteFolder(
                id,
                rd,
                state,
                folderUpserts,
                folderDeletes,
                v => (maxLm = Math.max(maxLm, v))
              )
            )
              stateDirty = true;
          },
        }
      );
    }
  }

  // --- 2. Pull everything changed since the cursor + merge (LWW) ------------
  const promptSnap = await getDocs(
    query(collection(db, promptsCol), where('last_modified', '>', state.cursor))
  );
  for (const ds of promptSnap.docs) {
    maxLm = Math.max(maxLm, (ds.data() as PromptDoc).last_modified || 0);
    if (
      applyRemotePrompt(
        ds.id,
        ds.data() as PromptDoc,
        state,
        promptUpserts,
        promptDeletes,
        () => {}
      )
    )
      stateDirty = true;
  }
  const folderSnap = await getDocs(
    query(collection(db, foldersCol), where('last_modified', '>', state.cursor))
  );
  for (const ds of folderSnap.docs) {
    maxLm = Math.max(maxLm, (ds.data() as FolderDoc).last_modified || 0);
    if (
      applyRemoteFolder(
        ds.id,
        ds.data() as FolderDoc,
        state,
        folderUpserts,
        folderDeletes,
        () => {}
      )
    )
      stateDirty = true;
  }

  if (maxLm > state.cursor) {
    state.cursor = maxLm;
    stateDirty = true;
  }

  // --- 3. Persist: apply pull/conflict changes onto a FRESH local read ------
  // Writes are skipped when nothing changed. Every storage.set produces new
  // bytes (fresh lastSync wrapper) and fires chrome.storage.onChanged, which
  // ApiSyncManager debounces into ANOTHER sync — unconditional writes turn
  // that into an endless self-triggering loop.
  const freshPrompts = ((await storage.get(STORAGE_KEYS.PROMPTS)) ?? []) as PromptData[];
  const freshPromptsById = new Map(freshPrompts.map((p): [string, PromptData] => [p.id, p]));
  for (const id of promptDeletes) freshPromptsById.delete(id);
  for (const [id, p] of promptUpserts)
    freshPromptsById.set(id, { ...freshPromptsById.get(id), ...p });

  const freshFolders = ((await storage.get(STORAGE_KEYS.FOLDERS)) ?? []) as FolderData[];
  const freshFoldersById = new Map(freshFolders.map((f): [string, FolderData] => [f.id, f]));
  for (const id of folderDeletes) freshFoldersById.delete(id);
  for (const [id, f] of folderUpserts)
    freshFoldersById.set(id, { ...freshFoldersById.get(id), ...f });

  const mergedPrompts = Array.from(freshPromptsById.values());
  if (JSON.stringify(mergedPrompts) !== JSON.stringify(freshPrompts)) {
    await storage.set(STORAGE_KEYS.PROMPTS, mergedPrompts);
  }
  const mergedFolders = Array.from(freshFoldersById.values());
  if (JSON.stringify(mergedFolders) !== JSON.stringify(freshFolders)) {
    await storage.set(STORAGE_KEYS.FOLDERS, mergedFolders);
  }
  if (stateDirty) {
    await storage.set(STORAGE_KEYS.API_SYNC_STATE, state);
  }
}

// Apply a remote prompt doc into the merge maps + meta, if it wins LWW.
// Returns true when the remote doc was applied (i.e. sync state changed).
function applyRemotePrompt(
  id: string,
  d: PromptDoc,
  state: SyncState,
  upserts: Map<string, PromptData>,
  deletes: Set<string>,
  noteLm: (lm: number) => void
): boolean {
  const incoming = { version: d.version ?? 0, lastModified: d.last_modified ?? 0 };
  if (!shouldApply(state.prompts[id], incoming)) return false;
  noteLm(incoming.lastModified);
  if (d.is_deleted) {
    deletes.add(id);
    upserts.delete(id);
    state.prompts[id] = { ...incoming, hash: '', deleted: true };
  } else {
    const local = docToPrompt(id, d);
    upserts.set(id, local);
    deletes.delete(id);
    state.prompts[id] = { ...incoming, hash: hashPrompt(local) };
  }
  return true;
}
function applyRemoteFolder(
  id: string,
  d: FolderDoc,
  state: SyncState,
  upserts: Map<string, FolderData>,
  deletes: Set<string>,
  noteLm: (lm: number) => void
): boolean {
  const incoming = { version: d.version ?? 0, lastModified: d.last_modified ?? 0 };
  if (!shouldApply(state.folders[id], incoming)) return false;
  noteLm(incoming.lastModified);
  if (d.is_deleted) {
    deletes.add(id);
    upserts.delete(id);
    state.folders[id] = { ...incoming, hash: '', deleted: true };
  } else {
    const local = docToFolder(id, d);
    upserts.set(id, local);
    deletes.delete(id);
    state.folders[id] = { ...incoming, hash: hashFolder(local) };
  }
  return true;
}

interface PushCallbacks<D> {
  onApplied: () => void;
  onRemoteWins: (remote: D) => void;
}

async function pushPrompt(
  db: ReturnType<typeof getFirestoreDb>,
  col: string,
  id: string,
  docData: PromptDoc,
  incoming: { version: number; lastModified: number },
  cb: PushCallbacks<PromptDoc>
) {
  const result = await runTransaction(db, async tx => {
    const ref = doc(db, col, id);
    const snap = await tx.get(ref);
    const existing = snap.exists()
      ? {
          version: (snap.data() as PromptDoc).version ?? 0,
          lastModified: (snap.data() as PromptDoc).last_modified ?? 0,
        }
      : undefined;
    if (shouldApply(existing, incoming)) {
      tx.set(ref, docData);
      return { applied: true as const };
    }
    return { applied: false as const, remote: snap.data() as PromptDoc };
  });
  if (result.applied) cb.onApplied();
  else cb.onRemoteWins(result.remote);
}

async function pushFolder(
  db: ReturnType<typeof getFirestoreDb>,
  col: string,
  id: string,
  docData: FolderDoc,
  incoming: { version: number; lastModified: number },
  cb: PushCallbacks<FolderDoc>
) {
  const result = await runTransaction(db, async tx => {
    const ref = doc(db, col, id);
    const snap = await tx.get(ref);
    const existing = snap.exists()
      ? {
          version: (snap.data() as FolderDoc).version ?? 0,
          lastModified: (snap.data() as FolderDoc).last_modified ?? 0,
        }
      : undefined;
    if (shouldApply(existing, incoming)) {
      tx.set(ref, docData);
      return { applied: true as const };
    }
    return { applied: false as const, remote: snap.data() as FolderDoc };
  });
  if (result.applied) cb.onApplied();
  else cb.onRemoteWins(result.remote);
}

function deletedPromptDoc(version: number, lm: number): PromptDoc {
  return {
    title: '',
    text: '',
    description: '',
    tags: [],
    folder: '',
    comments: '',
    last_modified: lm,
    version,
    is_deleted: true,
  };
}
function deletedFolderDoc(version: number, lm: number): FolderDoc {
  return {
    name: '',
    color: '',
    is_special: false,
    icon: null,
    type: null,
    parent_id: null,
    order: null,
    last_modified: lm,
    version,
    is_deleted: true,
  };
}
